<template>
  <div>
    <v-dialog v-model="showSelectTable" width="500" persistent>
      <v-card>
        <v-card-title>
          <h3 class="text-lg font-bold">
            Insert table
          </h3>
        </v-card-title>

        <v-card-text>
          <div class="d-flex">
            <v-text-field
                v-model="rows"
                class="pr-2"
                label="Rows"
                type="number"
                min="1"
                outlined
                dense
            ></v-text-field>
            <v-text-field
                v-model="columns"
                class="pl-2"
                label="Columns"
                type="number"
                min="1"
                outlined
                dense
            ></v-text-field>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="handleCancelButton">Cancel</v-btn>
          <v-btn color="primary" @click="handleInsertTable">Insert</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <basic-block :value="value" :mode="mode" :state.sync="state">
      <template v-slot:main>
        <portal :to="`table-toolbar-${value.id}`" v-if="mode === 'edit'">
          <table-toolbar v-if="isBeingEdited" :editor="editor"></table-toolbar>
        </portal>
        <div
            v-if="mode === 'edit'"
            class="lp-block-table-container"
            @click.stop="changeToEditable"
        >
          <editor-content
              class="lp-block-table"
              :class="{ 'lp-block-table-edit': mode === 'edit' }"
              :editor="editor"
              :style="style"
          />
        </div>
        <div v-else class="lp-block-table-container">
          <div class="lp-block-table">
            <component :is="tableBlock" :value="value" :mode="mode"/>
          </div>
        </div>
      </template>
    </basic-block>
  </div>

</template>

<script>
import BasicBlock from '../_components/containers/BasicBlock.vue';
import {EditorContent} from '@tiptap/vue-2';
import table from './mixins/table.vue';

import {renderKatex} from 'plant-common';
import TableToolbar from './TableToolbar.vue';
import {prefixCss} from "plant-common/src/utils";
import { BlockSelectionState } from '../_models/blockSelectionState';

export default {
  name: 'BlockTable',
  inject: ['blocksSelector', 'blockGlobals', 'lessonPlanController', 'blockRemover'],
  props: ['value', 'mode', 'blockVariables'],
  components: {
    EditorContent,
    BasicBlock,
    TableToolbar
  },
  mixins: [table],
  data() {
    return {
      editor: null,
      editable: false,
      state: BlockSelectionState.notSelected,
      showSelectTable: false,
      rows: 3,
      columns: 3,
    };
  },
  computed: {
    isBeingEdited() {
      return this.state === BlockSelectionState.editContent;
    },
    tableBlock() {
      return {
        template: `
          <div class="view">
            <div v-html="valueHtmlTransformation" ref="tiptap"></div>
          </div>`,
        props: ['value', 'blockVariables', 'mode'],
        computed: {
          valueHtmlTransformation() {
            let valueHtml = this.value.data.html;
            if (this.blockVariables) {
              this.blockVariables.forEach(bVar => {
                valueHtml = valueHtml.replace('{{' + bVar.name + '}}', bVar.value);
              });
            }

            return valueHtml;
          }
        },
        mounted() {
          this.renderFormulas();
        },
        methods: {
          renderFormulas() {
            if (!this.$refs.tiptap) return;
            renderKatex(this.$refs.tiptap);
          }
        }
      };
    },
    style() {
      return {};
    }
  },
  created() {
    this.initBlockCss();

    if (this.mode === 'edit') {
      this.initEditor();
      this.editor.setEditable(false);
    }

    if(!this.value.data.borderColor) {
      this.$set(this.value.data, "borderColor", "rgb(0, 0, 0)");
    }
  },
  watch: {
    state() {
      if (this.state !== BlockSelectionState.editContent) {
        this.editor.setEditable(false);
      }
    },
    'value.data': {
      handler() {
        const style = document.getElementById(`${this.value.type}-${this.value.id}`);

        if (!style) {
          return;
        }

        this.updateStyles(style)
      },
      deep: true,
      immediate: true,
    }
  },
  beforeDestroy() {
    if (this.editor) this.editor.destroy();
  },
  methods: {
    initEditor() {
      if (this.editor) {
        this.editor.destroy();
      }
      this.editor = this.setEditor(this.value.data);

      const isEmptyData = this.value.data.html === '' || this.value.data.html === '<p></p>';
      if (isEmptyData) {
        this.showSelectTable = true;
      }
    },
    updateStyles(element) {
      const {headerColor, cellPadding, borderStyle, borderThickness, borderColor} = this.value.data;

      element.innerHTML = prefixCss(`
            .lp-block-table table td,
            .lp-block-table table th {
              border: ${borderThickness}px solid rgb(0, 0, 0);
              border-style: ${borderStyle};
              border-color: ${borderColor};
              box-sizing: border-box;
              min-width: 1em;
              padding: ${cellPadding[0]}px ${cellPadding[1]}px ${cellPadding[2]}px ${cellPadding[3]}px;
            }

            .lp-block-table table th {
              background-color: ${headerColor};
              font-weight: normal;
              text-align: left;
            }
          `, `parent-${this.value.id}`);
    },
    initDataValuesIfNeeded() {
      if (this.value.data.headerColor === undefined) this.$set(this.value.data, 'headerColor', 'lightgrey');
      if (this.value.data.cellPadding === undefined) this.$set(this.value.data, 'cellPadding', [10, 10, 10, 10]);
      if (this.value.data.paddingLocked === undefined) this.$set(this.value.data, 'paddingLocked', true);
      if (this.value.data.borderStyle === undefined) this.$set(this.value.data, 'borderStyle', 'solid');
      if (this.value.data.borderThickness === undefined) this.$set(this.value.data, 'borderThickness', 2);
    }, 
    initBlockCss() {
      let style = document.getElementById(`${this.value.type}-${this.value.id}`);

      if (!style) {
        style = document.createElement('style');
        style.id = `${this.value.type}-${this.value.id}`;
        style.type = 'text/css';
        document.head.appendChild(style);
      }

      this.initDataValuesIfNeeded();
      this.updateStyles(style)
    },
    changeToEditable() {
      this.editor.setEditable(this.state === BlockSelectionState.editContent);
    },
    handleInsertTable() {
      this.editor
          .chain()
          .focus()
          .insertTable({rows: this.rows, cols: this.columns, withHeaderRow: true})
          .run();
      this.showSelectTable = false;
    },
    handleCancelButton() {
      this.showSelectTable = false;
      this.blocksSelector.selectParentBlock(this.value.id)
      this.blockRemover.deleteBlock(this.value.id);
    }
  }
};
</script>

<style>
.lp-block-table-container {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  -ms-overflow-style: none; /* Hide scrollbar IE and Edge */
  scrollbar-width: none; /* Hide scrollbar Firefox */
}

/* Hide scrollbar for Chrome, Safari and Opera */
.lp-block-table-container::-webkit-scrollbar {
  display: none;
}

.lp-block-table {
  font-size: 30px;
  line-height: 1.2;
  display: flex;
  width: 100%;
  height: 100%;
}

.lp-block-table .ProseMirror p:last-child,
.lp-block-table .view p:last-child {
  margin-bottom: 0;
}

.lp-block-table .ProseMirror,
.lp-block-table .view {
  width: 100%;
  word-wrap: break-word;
  white-space: pre-wrap;
}

.lp-block-table .ProseMirror > *,
.lp-block-table .view > *,
.lp-block-table table {
  width: 100%;
  height: 100%;
}

.lp-block-table table {
  border-collapse: collapse;
  overflow: hidden;
  table-layout: fixed;
  overflow-x: auto;
}

.lp-block-table table td,
.lp-block-table table th {
  border: 2px solid rgb(0, 0, 0);
  box-sizing: border-box;
  min-width: 1em;
  padding: 6px 8px;
}

.lp-block-table table td > *,
.lp-block-table table th > * {
  margin-bottom: 0;
}

.lp-block-table table .selectedCell {
  content: "";
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  pointer-events: none;
  position: absolute;
  z-index: 2;
}

.lp-block-table table th.selectedCell {
  background: rgba(0, 0, 0, 0.32);
  position: relative;
  filter: contrast(0.5);
}

.lp-block-table table td.selectedCell {
  background: rgba(212, 212, 212, 0.44);
  position: relative;
  filter: contrast(0.5);
}

.lp-block-table table .column-resize-handle {
  background-color: darkslategrey;
  bottom: -2px;
  pointer-events: none;
  position: absolute;
  right: -2px;
  top: 0;
  width: 4px;
}

.lp-block-table .resize-cursor {
  cursor: ew-resize;
  cursor: col-resize;
}

.lp-block-table ul,
.lp-block-table ol {
  padding-left: 80px;
  display: inline-block;
}

.lp-block-table-edit .fields:hover {
  background-color: #eeeeee;
  position: relative;
}

/* Tooltip */
.lp-block-table-edit .fields:before {
  content: attr(data-field);
  position: absolute;
  bottom: 100%;
  transform: translateX(50%);
  right: 50%;
  padding: 10px;
  width: 100%;
  min-width: 300px;
  background-color: #ffffff;
  text-align: left;
  font-size: 30px;
  display: none;
}

.lp-block-table-edit .fields:hover:before {
  display: block;
}

.lp-block-table-edit span[data-inline-katex] {
  background: rgb(235, 235, 235);
  padding: 2px;
  display: inline-block;
}

.lp-block-table-edit .katex-wrapper table {
  font-size: 2.2rem;
}

.lp-block-table-edit .katex-component__title {
  font-size: 30px;
}

.lp-block-table-edit .katex-component__title .v-icon {
  font-size: 42px;
}

.lp-block-table-edit .katex-component__title button {
  width: 48px !important;
  height: 48px;
}
</style>
